import { Link } from "react-router-dom"
import { MessageCircle, ArrowUp, Bookmark, Clock } from "lucide-react"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { formatRelativeTime, getInitials, formatNumber } from "@/lib/utils"

// Helper to extract date from Firestore Timestamp or ISO string
function getDate(value: unknown): Date {
  if (!value) return new Date()
  if (typeof value === "string") return new Date(value)
  if (value instanceof Date) return value
  if (typeof value === "object" && "toDate" in value && typeof (value as { toDate: () => Date }).toDate === "function") {
    return (value as { toDate: () => Date }).toDate()
  }
  return new Date()
}

interface PostCardProps {
  post: {
    id: string
    title: string
    slug: string
    excerpt?: string
    subtitle?: string
    coverImage?: string
    tags?: string[]
    readingTime?: number
    upvoteCount?: number
    commentCount?: number
    publishedAt?: unknown
    createdAt?: unknown
    author?: {
      username?: string
      displayName?: string
      avatarUrl?: string
    }
  }
  showCover?: boolean
}

export function PostCard({ post, showCover = true }: PostCardProps) {
  return (
    <Card className="overflow-hidden transition-shadow hover:shadow-md">
      <article className="flex flex-col sm:flex-row">
        {/* Cover Image */}
        {showCover && post.coverImage && (
          <Link to={`/post/${post.slug}`} className="relative aspect-video w-full sm:aspect-square sm:w-48 flex-shrink-0 overflow-hidden">
            <img src={post.coverImage} alt={post.title} className="h-full w-full object-cover" />
          </Link>
        )}

        {/* Content */}
        <div className="flex flex-1 flex-col p-4 sm:p-5">
          {/* Author & Meta */}
          <div className="flex items-center space-x-2 mb-3">
            <Link to={`/user/${post.author?.username}`}>
              <Avatar className="h-8 w-8">
                <AvatarImage src={post.author?.avatarUrl} alt={post.author?.displayName} />
                <AvatarFallback>{getInitials(post.author?.displayName || "U")}</AvatarFallback>
              </Avatar>
            </Link>
            <div className="flex flex-col">
              <Link
                to={`/user/${post.author?.username}`}
                className="text-sm font-medium text-gray-900 hover:text-indigo-600 dark:text-white dark:hover:text-indigo-400"
              >
                {post.author?.displayName}
              </Link>
              <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                <span>{formatRelativeTime(getDate(post.publishedAt || post.createdAt))}</span>
                <span className="mx-1">·</span>
                <span className="flex items-center">
                  <Clock className="mr-1 h-3 w-3" />
                  {post.readingTime} min read
                </span>
              </div>
            </div>
          </div>

          {/* Title & Excerpt */}
          <Link to={`/post/${post.slug}`} className="group flex-1">
            <h2 className="mb-2 text-xl font-bold text-gray-900 group-hover:text-indigo-600 dark:text-white dark:group-hover:text-indigo-400 line-clamp-2">
              {post.title}
            </h2>
            {post.subtitle && <p className="text-gray-600 dark:text-gray-400 line-clamp-1 mb-2">{post.subtitle}</p>}
            <p className="text-gray-500 dark:text-gray-400 line-clamp-2 text-sm">{post.excerpt}</p>
          </Link>

          {/* Tags & Actions */}
          <div className="mt-4 flex items-center justify-between">
            <div className="flex flex-wrap gap-2">
              {post.tags?.slice(0, 3).map(tag => (
                <Link key={tag} to={`/tag/${tag}`}>
                  <Badge variant="secondary" className="text-xs hover:bg-gray-200 dark:hover:bg-gray-700">
                    #{tag}
                  </Badge>
                </Link>
              ))}
            </div>

            <div className="flex items-center space-x-3 text-gray-500 dark:text-gray-400">
              <span className="flex items-center text-sm">
                <ArrowUp className="mr-1 h-4 w-4" />
                {formatNumber(post.upvoteCount || 0)}
              </span>
              <span className="flex items-center text-sm">
                <MessageCircle className="mr-1 h-4 w-4" />
                {formatNumber(post.commentCount || 0)}
              </span>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <Bookmark className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </article>
    </Card>
  )
}
